/**
 * Offset resolution from the shipped transition table.
 *
 * Every instant is stored in UTC (CODE-RULES 3.2). Local time only exists at
 * the moment of display, and the offset that produces it depends on *when* —
 * Sydney is UTC+10 in July and UTC+11 in January. These functions answer
 * "which offset was in force at this instant" and nothing else.
 *
 * Deterministic and dependency-free on purpose: the Gantt has a 500ms budget
 * for drag feedback, so this runs in the browser, and the same code has to
 * give the same answer on the server. `Intl.DateTimeFormat` is deliberately
 * not used — its rules come from whatever tzdata the host happens to carry,
 * which is exactly the kind of invisible drift the Flight Engine cannot have.
 */

/** One transition: [instant (unix seconds), abbreviation, offset (s), dst flag]. */
export type ZoneTransition = [at: number, abbreviation: string, offset: number, dst: 0 | 1]

/** A zone as shipped: the state at the start of the window, then every change. */
export interface ZoneRules {
  /** State in force at the start of the window: [abbreviation, offset, dst]. */
  b: [abbreviation: string, offset: number, dst: 0 | 1]
  /** Transitions inside the window, ascending by instant. */
  t: ZoneTransition[]
}

/** Offset table for every zone, keyed by IANA name. */
export type ZoneRulesTable = Record<string, ZoneRules>

export interface ZoneState {
  /** IANA zone the state belongs to. */
  zone: string
  /** e.g. "ICT", "AEDT". Display only — abbreviations are not unique. */
  abbreviation: string
  /** Seconds east of UTC. Add to a UTC instant to get local time. */
  offsetSeconds: number
  isDst: boolean
  /** Start of this state, unix ms. Null when it predates the window. */
  since: number | null
  /** Next change, unix ms. Null when nothing further is known. */
  until: number | null
}

/**
 * The state in force at `utcMs`.
 *
 * Binary search over the transitions, so a 40-year window costs ~6 comparisons
 * and a Gantt row can resolve thousands of instants inside its frame budget.
 */
export function zoneStateAt(zone: string, rules: ZoneRules, utcMs: number): ZoneState {
  const seconds = Math.floor(utcMs / 1000)
  const transitions = rules.t

  let low = 0
  let high = transitions.length - 1
  let found = -1
  while (low <= high) {
    const mid = (low + high) >> 1
    if (transitions[mid][0] <= seconds) {
      found = mid
      low = mid + 1
    } else {
      high = mid - 1
    }
  }

  const next = transitions[found + 1]
  const until = next ? next[0] * 1000 : null

  if (found < 0) {
    return {
      zone,
      abbreviation: rules.b[0],
      offsetSeconds: rules.b[1],
      isDst: rules.b[2] === 1,
      since: null,
      until,
    }
  }

  const [at, abbreviation, offset, dst] = transitions[found]
  return {
    zone,
    abbreviation,
    offsetSeconds: offset,
    isDst: dst === 1,
    since: at * 1000,
    until,
  }
}

/**
 * The instant a wall-clock reading names — the inverse of `zoneStateAt`.
 *
 * Needed because operational files are written in station local time
 * ("Times in Local Station" on the daily report), while storage is UTC
 * (CODE-RULES 3.2). The offset that converts a reading depends on the instant
 * the reading turns out to be, so the offset is resolved twice: once at the
 * naive instant, then again at the candidate it produces. Two passes settle
 * every case a fixed offset would get wrong by an hour.
 *
 * Two readings have no single answer, and both are resolved the same way every
 * time rather than left to chance:
 *   - A clock that sprang forward never showed 02:30; the reading is mapped
 *     through the offset after the change, landing just past the gap.
 *   - A clock that fell back showed 01:30 twice; the earlier instant wins,
 *     which keeps a sequence of readings monotonic.
 */
export function utcFromLocal(zone: string, rules: ZoneRules, parts: LocalInput): number {
  const naive = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, 0)
  const first = zoneStateAt(zone, rules, naive).offsetSeconds
  const candidate = naive - first * 1000
  const second = zoneStateAt(zone, rules, candidate).offsetSeconds
  if (second === first) return candidate

  // The offset changed between the two probes: the reading sits near a
  // transition. Accept the refined candidate only when it is self-consistent —
  // it is the earlier instant on a fall-back, and the post-change instant on a
  // spring-forward gap.
  const refined = naive - second * 1000
  return zoneStateAt(zone, rules, refined).offsetSeconds === second ? refined : candidate
}

/** Wall-clock fields a reading carries. Seconds are never in an ops file. */
export interface LocalInput {
  year: number
  month: number
  day: number
  hour: number
  minute: number
}

/**
 * Every transition strictly inside [fromMs, toMs).
 *
 * This is the one the engine needs: a turnaround, a duty period or a
 * maintenance slot that straddles a clock change gains or loses an hour of
 * wall-clock time, and R1 (minimum ground time) has to be judged on elapsed
 * UTC rather than on the local clock.
 */
export function transitionsBetween(
  rules: ZoneRules,
  fromMs: number,
  toMs: number,
): ZoneTransition[] {
  const from = Math.floor(fromMs / 1000)
  const to = Math.floor(toMs / 1000)
  return rules.t.filter(([at]) => at >= from && at < to)
}

/** "+07:00", "-04:30", "+00:00". */
export function formatOffset(offsetSeconds: number): string {
  const sign = offsetSeconds < 0 ? '-' : '+'
  const total = Math.abs(Math.round(offsetSeconds / 60))
  const hours = Math.floor(total / 60)
  const minutes = total % 60
  return `${sign}${pad(hours)}:${pad(minutes)}`
}

export interface LocalParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

/** Wall-clock fields of `utcMs` seen at `offsetSeconds`. */
export function localPartsAt(utcMs: number, offsetSeconds: number): LocalParts {
  const shifted = new Date(utcMs + offsetSeconds * 1000)
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
    second: shifted.getUTCSeconds(),
  }
}

/** "2026-09-10 14:05" — the shape the Gantt axis and every table use. */
export function formatLocal(utcMs: number, offsetSeconds: number): string {
  const p = localPartsAt(utcMs, offsetSeconds)
  return `${p.year}-${pad(p.month)}-${pad(p.day)} ${pad(p.hour)}:${pad(p.minute)}`
}

/** "2026-09-10 14:05 +07:00" — used wherever the zone must be unambiguous. */
export function formatLocalWithOffset(utcMs: number, offsetSeconds: number): string {
  return `${formatLocal(utcMs, offsetSeconds)} ${formatOffset(offsetSeconds)}`
}

/** "2026-09-10 07:05Z". */
export function formatUtc(utcMs: number): string {
  return `${formatLocal(utcMs, 0)}Z`
}

function pad(value: number): string {
  return value < 10 ? `0${value}` : String(value)
}
